/**
 * AuthInput — Brand-styled form input for auth screens.
 *
 * Specs from auth-screens.jsx design:
 * - Label: 13px, 600 weight, Plus Jakarta Sans, color var(--t2)
 * - Input: 44px height, 14px/500 text, 12px border-radius
 * - Focus: 1.5px indigo border + 3px rgba(79,70,229,0.08) glow
 * - Optional left icon, right icon (e.g. eye toggle), error, hint
 *
 * @param label      — Field label text.
 * @param icon       — ReactNode rendered at left inside the input.
 * @param rightIcon  — ReactNode rendered at right (e.g. password toggle).
 * @param onRightIconClick — Click handler for the right icon button.
 * @param error      — Error message string (renders red below input).
 * @param hint       — Hint message string (renders muted below input).
 */

import { useState, type InputHTMLAttributes, type ReactNode } from 'react';

interface AuthInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Field label text */
  label?: string;
  /** Icon rendered at the left edge inside the input */
  icon?: ReactNode;
  /** Icon rendered at the right edge inside the input */
  rightIcon?: ReactNode;
  /** Click handler for the right icon button */
  onRightIconClick?: () => void;
  /** Error message (takes priority over hint) */
  error?: string;
  /** Hint message shown below input */
  hint?: string;
  /**
   * [OCCASION-CARDS-1 Slice 2] Content painted INSIDE the input's own box — used by the rotating
   * placeholder overlay.
   *
   * It has to live here rather than beside <AuthInput>, because this component renders a LABEL above
   * the field. An overlay positioned against the outer wrapper centres itself across label + gap +
   * input and therefore sits above the input's text — which is exactly the misalignment this fixes.
   * Rendered in the same relative box as the left icon, so it inherits the one positioning context
   * that is already correct.
   */
  overlay?: ReactNode;
}

export function AuthInput({
  label,
  icon,
  rightIcon,
  onRightIconClick,
  error,
  hint,
  overlay,
  ...inputProps
}: AuthInputProps) {
  const [focused, setFocused] = useState(false);

  const borderColor = error
    ? 'var(--red)'
    : focused
      ? 'var(--p)'
      : 'var(--border)';

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          className="font-body text-[13px] font-semibold tracking-[0.01em]"
          style={{ color: 'var(--t2)' }}
        >
          {label}
        </label>
      )}

      <div className="relative">
        {/* Left icon */}
        {icon && (
          <div
            className="absolute left-[13px] top-1/2 -translate-y-1/2 flex items-center transition-colors duration-150"
            style={{ color: focused ? 'var(--p)' : 'var(--t4)' }}
          >
            {icon}
          </div>
        )}

        <input
          {...inputProps}
          onFocus={(e) => {
            setFocused(true);
            inputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            inputProps.onBlur?.(e);
          }}
          className={`auth-field${icon ? ' auth-field--icon' : ''}${rightIcon ? ' auth-field--right' : ''} w-full font-body outline-none transition-all duration-150`}
          style={{
            height: 44,
            paddingLeft: icon ? 42 : 14,
            paddingRight: rightIcon ? 44 : 14,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--t1)',
            background: 'var(--surface)',
            border: `1.5px solid ${borderColor}`,
            borderRadius: 'var(--radius-md)',
            boxShadow: focused
              ? '0 0 0 3px rgba(79,70,229,0.08)'
              : 'none',
          }}
        />

        {/*
          Overlay, inside the input's box. It is given the SAME left offset the input uses for its
          own text (42 with an icon, 14 without), so the hint starts exactly where the typed
          character will — not a hardcoded guess that drifts the moment the icon changes.
        */}
        {overlay && (
          <div
            aria-hidden="true"
            className="absolute inset-y-0 flex items-center pointer-events-none"
            style={{ left: icon ? 42 : 14, right: rightIcon ? 44 : 14 }}
          >
            {overlay}
          </div>
        )}

        {/* Right icon */}
        {rightIcon && (
          <button
            type="button"
            onClick={onRightIconClick}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center p-0 bg-transparent border-none cursor-pointer"
            style={{ color: 'var(--t3)' }}
            tabIndex={-1}
          >
            {rightIcon}
          </button>
        )}
      </div>

      {/* Error text */}
      {error && (
        <p
          className="font-body text-xs font-medium m-0"
          style={{ color: 'var(--red)' }}
        >
          {error}
        </p>
      )}

      {/* Hint text */}
      {hint && !error && (
        <p
          className="font-body text-xs font-medium m-0"
          style={{ color: 'var(--t3)' }}
        >
          {hint}
        </p>
      )}
    </div>
  );
}

export default AuthInput;